import { createRef, useEffect, useRef, useState } from 'react';
import { OrderModel } from '../../../models/order';
import { DEFAULT_ROWS_PER_PAGE } from '../../../config-global';
import axios from '../../../lib/axios';
import { ADMIN_ORDER_FIND_MANY } from '../../../lib/endpoints/order';
import { ReportEnum } from '../../../enums/reportEnum';
import { OrderStatusEnum } from '../../../enums/orderStatus';
import { MerChantModel } from '../../../models/merChantModel';
import { MERCHANT_STORE_FIND_MANY } from '../../../lib/endpoints/merchant';
import {
  convertOrderStatusCodeToOrderStatusEnum,
  convertOrderStatusEnumToStatusCode,
} from '../../../utils/convertOrderStatusValue';
import { RootState } from '../../../store';
import { useSelector } from 'react-redux';
import { convertDateStringToNormalStringMDY } from '../../../utils/convertDateFormat';
import { calculateOrderProductUnitByCarts } from '../../../utils/calculateOrderProductUnitByCarts';
import { concatenateStrings } from '../../../utils/concatenateStrings';
import { convertPromotionTypeToText } from '../../../utils/convertPromotionTypeToText';
import moment from 'moment';
import { SuppliesMainGroupName } from '../../../models/suppliesMain';
import { SuppliesDivisionModel } from '../../../models/suppliesDivisionModel';
import { SuppliesCategoryProduct } from '../../../models/suppliesCategoryProduct';
import { SuppliesProductSubCategory } from '../../../models/suppliesSubCategory';
import {
  MASTER_CATEGORIES_FIND_MANY,
  MASTER_DIVISION_FIND_MANY,
  MASTER_MAIN_GROUP_FIND_MANY,
  MASTER_SUB_CATEGORIES_FIND_MANY,
} from '../../../lib/endpoints/category';

interface ReportStatisticType {
  all: number;
  delivered: number;
  partiallyCompletedSale: number;
  returnSale: number;
  failedSale: number;
}

function filterDataByDate(data: OrderModel[], startDate: Date, endDate: Date): OrderModel[] {
  return data.filter((item) => {
    const updatedAt = new Date(item.updatedAt).setHours(0, 0, 0, 0);
    return updatedAt >= startDate.setHours(0, 0, 0, 0) && updatedAt <= endDate.setHours(0, 0, 0, 0);
  });
}

function calculateTotalWithDeliveryFeeSum(orders: OrderModel[]): number {
  return orders.reduce((sum, order: OrderModel) => sum + order?.totoalWithDeliveryFee, 0);
}

const useReportController = () => {
  const [reportData, setReportData] = useState<OrderModel[]>([]);
  const [filterData, setFilterData] = useState<OrderModel[]>([]);
  const [merchantData, setMerchantData] = useState<MerChantModel[]>([]);
  const [merchantValue, setMerchantValue] = useState<string>('All');
  const [orderStatusValue, setOrderStatusValue] = useState<OrderStatusEnum>(OrderStatusEnum.All);
  const [selectedId, setSelectedId] = useState<string>(null!);
  const [selectedReportData, setSelectedReportData] = useState<OrderModel>(null!);
  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [openMenu, setOpenMenu] = useState(null);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [reportType, setReportType] = useState<ReportEnum>(ReportEnum.SaleReport);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const buttonExportCsv = createRef<any>();
  const [showInMoney, setShowInMoney] = useState<string>('amount');
  const [dataReportStatistic, setDataReportStatistic] = useState<ReportStatisticType>({
    all: 0,
    delivered: 0,
    partiallyCompletedSale: 0,
    returnSale: 0,
    failedSale: 0,
  });
  const [dataMasterMainGroups, setDataMasterMainGroups] = useState<SuppliesMainGroupName[]>([]);
  const [dataMasterDivisions, setDataMasterDivisions] = useState<SuppliesDivisionModel[]>([]);
  const [dataMasterCategories, setDataMasterCategories] = useState<SuppliesCategoryProduct[]>([]);
  const [dataMasterSubCategories, setDataMasterSubCategories] = useState<
    SuppliesProductSubCategory[]
  >([]);

  const startDateRef = useRef<HTMLInputElement>(null!);
  const endDateRef = useRef<HTMLInputElement>(null!);
  const orderId = useRef<HTMLInputElement>(null!);

  const assignMerchantToAdmin = useSelector((state: RootState) => state?.auth?.data?.merchants);
  const adminRole = useSelector((state: RootState) => state.auth?.data?.role?.role_type);
  const authData = useSelector((state: RootState) => state?.auth?.data);

  const buildQueryParams = () => {
    const startDateValue = startDateRef.current.value;
    const endDateValue = endDateRef.current.value;

    const queryParams = new URLSearchParams();

    if (startDateValue) {
      const adjustedStartDate = moment(new Date(startDateValue)).subtract(1, 'days');
      queryParams.set('startDate', adjustedStartDate.format('MM/DD/YYYY'));
    }

    if (endDateValue) {
      queryParams.set('endDate', moment(new Date(endDateValue)).format('MM/DD/YYYY'));
    }

    return queryParams.toString();
  };

  const applyFilters = (data: any[]) => {
    let newData = [...data];

    newData = filterDataByDate(
      newData,
      new Date(startDateRef.current.value),
      new Date(endDateRef.current.value)
    );

    if (orderStatusValue === OrderStatusEnum.Partially) {
      newData = newData.filter(
        (item) =>
          item?.updateByAdmin &&
          item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered)
      );
    } else if (orderStatusValue === OrderStatusEnum.Delivered) {
      newData = newData.filter(
        (item) =>
          !item?.updateByAdmin &&
          item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered)
      );
    } else if (orderStatusValue !== OrderStatusEnum.All) {
      newData = newData.filter(
        (item) => orderStatusValue === convertOrderStatusCodeToOrderStatusEnum(item?.status?.code)
      );
    }

    if (merchantValue !== 'All') {
      newData = newData.filter((item) => item?.merchantId === merchantValue);
    }

    return newData;
  };

  const handleFilterData = async () => {
    try {
      const statusCodes = [6, 7, 8];
      const statusQueryParams = statusCodes.map((code) => `StatusCodeAdmin=${code}`).join('&');
      const queryParams = buildQueryParams();
      const fullQueryParams = `${statusQueryParams}${queryParams ? `&${queryParams}` : ''}`;
      const formData: any = {
        ...(merchantValue === 'All' && { merchantId: merchantValue }),
      };
      const apiEndpoint = ADMIN_ORDER_FIND_MANY + (fullQueryParams ? `?${fullQueryParams}` : '');
      const resData = await axios.post(apiEndpoint, formData);
      const filteredData = applyFilters(resData.data);
      setFilterData(filteredData);
    } catch (err) {
      console.error(err);
    }
  };

  const handleGetData = async () => {
    try {
      setLoadingData(true);

      // find categories
      const resMasterMainGroups = await axios.post(MASTER_MAIN_GROUP_FIND_MANY);
      const resMasterDivisions = await axios.post(MASTER_DIVISION_FIND_MANY);
      const resMasterCategories = await axios.post(MASTER_CATEGORIES_FIND_MANY);
      const resMasterSubCategories = await axios.post(MASTER_SUB_CATEGORIES_FIND_MANY);

      const filterReportData = reportData.filter(
        (item) =>
          item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered) ||
          item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Failed) ||
          item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Return)
      );

      const merchantRes = await axios.get(MERCHANT_STORE_FIND_MANY);

      if (resMasterMainGroups?.data?.length > 0) {
        setDataMasterMainGroups(resMasterMainGroups?.data);
      }
      if (resMasterDivisions?.data?.length > 0) {
        setDataMasterDivisions(resMasterDivisions?.data);
      }
      if (resMasterCategories?.data?.length > 0) {
        setDataMasterCategories(resMasterCategories?.data);
      }
      if (resMasterSubCategories?.data?.length > 0) {
        setDataMasterSubCategories(resMasterSubCategories?.data);
      }

      if (merchantRes?.data?.length > 0) {
        const matchingMerchants = merchantRes.data.filter((merchant: any) =>
          assignMerchantToAdmin?.includes(merchant?.id)
        );

        if (adminRole === 'SUPER_ADMIN') {
          setMerchantData(merchantRes?.data);
          setFilterData(filterDataByDate(filterReportData, new Date(), new Date()));
          setReportData(filterReportData);
        } else if (adminRole === 'ADMIN') {
          if (matchingMerchants.length > 0) {
            setMerchantData(matchingMerchants);
            const filterReportDataByMerchant: OrderModel[] = filterReportData.filter((item) =>
              authData?.merchants.includes(item?.merchantId)
            );
            setFilterData(filterDataByDate(filterReportDataByMerchant, new Date(), new Date()));
            setReportData(filterReportDataByMerchant);
          }
        }
      }
    } catch (err) {
      // console.log(err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleFetchDataByOrderId = async () => {
    try {
      setLoadingData(true);
      setFilterData(reportData?.filter((item) => item?.orderNo === orderId.current.value));
    } catch (err) {
      // console.log(err);
    } finally {
      setLoadingData(false);
    }
  };

  const handleOpenForm = () => {
    setOpenMenu(null);
    setOpenForm(true);
    const getData = filterData?.filter((data: OrderModel) => data?.id === selectedId)[0];
    setSelectedReportData(getData);
  };

  const handleOpenMenu = (event: any, getId: string) => {
    setOpenMenu(event.currentTarget);
    setSelectedId(getId);
  };

  let filterDataByBranch: OrderModel[] = reportData;
  if (merchantValue === 'All') {
    filterDataByBranch = reportData;
  } else {
    filterDataByBranch = reportData?.filter(
      (item: OrderModel) =>
        merchantData
          .filter((subItem) => subItem?.id === item?.merchantId)
          ?.filter((branch) => branch?.id === merchantValue).length > 0
    );
  }

  const rows = filterData.slice(
    (currentPage - 1) * DEFAULT_ROWS_PER_PAGE,
    currentPage * DEFAULT_ROWS_PER_PAGE
  );

  const countPagination = Math.ceil(filterData.length / DEFAULT_ROWS_PER_PAGE);

  const isSearchNotFound: boolean = !filterData.length && !!searchValue;

  //----------filter status bar--------------//
  const totalMoney = filterData
    .map((order: OrderModel) => order.totoalWithDeliveryFee)
    .reduce((total, money) => total + money, 0);

  const filterFailOrder = filterData.filter((item: OrderModel) => item?.status?.code === 7);
  const totalMoneyFailOrder = filterFailOrder
    .map((item: OrderModel) => item?.totoalWithDeliveryFee)
    .reduce((total, money) => total + money, 0);

  const filterReturnOrder = filterData.filter((item: OrderModel) => item?.status?.code === 8);
  const totalMoneyReturnOrder = filterReturnOrder
    .map((item: OrderModel) => item?.totoalWithDeliveryFee)
    .reduce((total, money) => total + money, 0);

  const filterDeliveredOrder = filterData.filter((item: OrderModel) => item?.status?.code === 6);
  const totalMoneyDelivered = filterDeliveredOrder
    .map((item: OrderModel) => item?.totoalWithDeliveryFee)
    .reduce((total, money) => total + money, 0);

  const totalBCELOneCash = filterData
    .filter((item: OrderModel) => item?.paymentMethod === 'BCELONE')
    .map((item: OrderModel) => item?.totoalWithDeliveryFee)
    .reduce((total, money) => total + money, 0);

  const totalCash = filterData
    .filter((item: OrderModel) => item?.paymentMethod === 'CASH')
    .map((item: OrderModel) => item?.totoalWithDeliveryFee)
    .reduce((total, money) => total + money, 0);

  const totalUnPaidMoney = filterData
    .filter(
      (item: OrderModel) => typeof item?.isPaidStatus === 'boolean' && item.isPaidStatus === false
    )
    .map((item: OrderModel) => item?.totoalWithDeliveryFee)
    .reduce((total, money) => total + money, 0);

  //-------- useEffect----------//
  useEffect(() => {
    handleGetData();
    startDateRef.current.value = moment(new Date()).format('yyyy-MM-DD');
    endDateRef.current.value = moment(new Date()).format('yyyy-MM-DD');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showInMoney === 'money') {
      setDataReportStatistic({
        all: calculateTotalWithDeliveryFeeSum(
          filterData.filter(
            (item) =>
              item?.status?.code ===
                convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered) ||
              item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Return) ||
              item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Failed)
          )
        ),
        delivered: calculateTotalWithDeliveryFeeSum(
          filterData?.filter(
            (item) =>
              item?.status?.code ===
                convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered) &&
              item?.updateByAdmin === false
          )
        ),
        partiallyCompletedSale: calculateTotalWithDeliveryFeeSum(
          filterData?.filter(
            (item) =>
              item?.updateByAdmin === true &&
              item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered)
          )
        ),
        returnSale: calculateTotalWithDeliveryFeeSum(
          filterData?.filter(
            (item) =>
              item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Return)
          )
        ),
        failedSale: calculateTotalWithDeliveryFeeSum(
          filterData?.filter(
            (item) =>
              item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Failed)
          )
        ),
      });
    } else {
      setDataReportStatistic({
        all: filterData?.length,
        delivered: filterData?.filter(
          (item) =>
            item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered) &&
            item?.updateByAdmin === false
        )?.length,
        partiallyCompletedSale: filterData?.filter(
          (item) =>
            item?.updateByAdmin === true &&
            item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Delivered)
        )?.length,
        returnSale: filterData?.filter(
          (item) =>
            item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Return)
        )?.length,
        failedSale: filterData?.filter(
          (item) =>
            item?.status?.code === convertOrderStatusEnumToStatusCode(OrderStatusEnum.Failed)
        )?.length,
      });
    }
  }, [filterData, showInMoney]);

  // filter variables
  const findDataExportCsv = (): any => {
    let result = [];
    for (let i: number = 0; i < filterData?.length; i++) {
      for (let j: number = 0; j < filterData[i]?.carts?.length; j++) {
        const item = filterData[i];
        const el = filterData[i]?.carts[j];
        result.push({
          order_no: item?.orderNo,
          order_number: item?.orderNumber,
          shipment_method: item?.orderMethod,
          payment_method: item?.paymentMethod,
          total_unit: calculateOrderProductUnitByCarts(item?.carts),
          status: convertOrderStatusCodeToOrderStatusEnum(item?.status?.code),
          riderAssist: item?.riderAssist,
          riderDetail: item?.riderDetail,
          order_time: convertDateStringToNormalStringMDY(item?.createdAt),
          latest_update: convertDateStringToNormalStringMDY(
            item?.orderStatusTime?.dateTime?.at(-1)?.time ?? ''
          ),
          total_product_price: item?.totalNomalPrice,
          vax: item?.totalVatPrice,
          delivery_fee: item?.deliveryFee,
          discount_from_promotion: item?.totalDiscountPriceForPromotion,
          discount_from_coupon: item?.totalDiscountPriceForCoupon,
          total:
            item?.status?.code === 7 || item?.status?.code === 8
              ? `-${item?.totoalWithDeliveryFee}`
              : item?.totoalWithDeliveryFee,
          merchant_store: item?.merchant?.nameLa,
          product: el?.product?.price?.masterProduct?.productNameLa,
          barcode: concatenateStrings(el?.product?.price?.masterProduct?.barcodes),
          item_code: el?.product?.price?.masterProduct?.itemcode,
          product_quantity: el?.quantity,
          product_price: el?.productPrice,
          discount: el?.discount,
          promotion: convertPromotionTypeToText(el?.product?.promotion?.promotionType),
          total_product: el?.total,
          // main_group: dataMasterMainGroups?.filter(
          //   (mg) => mg?.id === el?.product?.price?.masterProduct?.masterMainGroupId
          // )[0]?.main_group_name_en,
          // division: dataMasterDivisions?.filter(
          //   (dv) => dv?.id === el?.product?.price?.masterProduct?.masterDivisionNameId
          // )[0]?.division_name_en,
          // category: dataMasterCategories?.filter(
          //   (cat) => cat?.id === el?.product?.price?.masterProduct?.masterProductCategoryId
          // )[0]?.category_name_en,
          // sub_category: dataMasterSubCategories?.filter(
          //   (sub) => sub?.id === el?.product?.price?.masterProduct?.masterProductSubCategoryId
          // )[0]?.sub_category_name_en,
        });
      }
    }
    return result;
  };
  // const findDataExportCsv = (): any => {
  //   let result = [];
  //   for (let i = 0; i < filterData?.length; i++) {
  //     for (let j = 0; j < filterData[i]?.carts?.length; j++) {
  //       const item = filterData[i];
  //       const el = filterData[i]?.carts[j];
  //       const masterProduct = el?.product?.price?.masterProduct;

  //       const mainGroup = dataMasterMainGroups?.find(
  //         (mg) => mg?.id === masterProduct?.masterMainGroupId
  //       )?.main_group_name_en;
  //       const division = dataMasterDivisions?.find(
  //         (dv) => dv?.id === masterProduct?.masterDivisionNameId
  //       )?.division_name_en;
  //       const category = dataMasterCategories?.find(
  //         (cat) => cat?.id === masterProduct?.masterProductCategoryId
  //       )?.category_name_en;
  //       const subCategory = dataMasterSubCategories?.find(
  //         (sub) => sub?.id === masterProduct?.masterProductSubCategoryId
  //       )?.sub_category_name_en;
  //       result.push({
  //         order_no: item?.orderNo,
  //         order_number: item?.orderNumber,
  //         shipment_method: item?.orderMethod,
  //         payment_method: item?.paymentMethod,
  //         total_unit: calculateOrderProductUnitByCarts(item?.carts),
  //         status: convertOrderStatusCodeToOrderStatusEnum(item?.status?.code),
  //         riderAssist: item?.riderAssist,
  //         riderDetail: item?.riderDetail,
  //         order_time: convertDateStringToNormalDateTime(item?.createdAt),
  //         total_product_price: item?.totalNomalPrice,
  //         vax: item?.totalVatPrice,
  //         delivery_fee: item?.deliveryFee,
  //         discount_from_promotion: item?.totalDiscountPriceForPromotion,
  //         discount_from_coupon: item?.totalDiscountPriceForCoupon,
  //         total:
  //           item?.status?.code === 7 || item?.status?.code === 8
  //             ? `-${item?.totoalWithDeliveryFee}`
  //             : item?.totoalWithDeliveryFee,
  //         merchant_store: item?.merchant?.nameLa,
  //         product: masterProduct?.productNameLa,
  //         barcode: concatenateStrings(masterProduct?.barcodes),
  //         item_code: masterProduct?.itemcode,
  //         product_quantity: el?.quantity,
  //         product_price: el?.productPrice,
  //         discount: el?.discount,
  //         promotion: convertPromotionTypeToText(el?.product?.promotion?.promotionType),
  //         total_product: el?.total,
  //         main_group: mainGroup,
  //         division: division,
  //         category: category,
  //         sub_category: subCategory,
  //       });
  //     }
  //   }
  //   return result;
  // };

  return {
    countPagination,
    isSearchNotFound,
    rows,
    handleCloseForm: () => setOpenForm(false),
    handleCloseMenu: () => setOpenMenu(null),
    handleOpenForm,
    handleOpenMenu,
    handleChangePage: (e: any, newPage: any) => setCurrentPage(newPage),
    currentPage,
    openForm,
    openMenu,
    handleSearch: (e: any) => {
      setCurrentPage(1);
      setSearchValue(e.target.value);
    },
    searchValue,
    totalMoney,
    totalMoneyFailOrder,
    filterFailOrder,
    filterReturnOrder,
    totalMoneyReturnOrder,
    reportData,
    filterDeliveredOrder,
    totalMoneyDelivered,
    selectedReportData,
    loadingData,
    reportType,
    setReportType,
    handleSelectedOrderStatus: (e: any) => {
      setOrderStatusValue(e.target.value);
      setCurrentPage(1);
    },
    orderStatusValue,
    handleSelectedMerchant: (newValue: string) => setMerchantValue(newValue),
    merchantValue,
    merchantData,
    filterDataByBranch,
    filterData,
    handleFetchDataByOrderId,
    orderId,
    totalBCELOneCash,
    totalCash,
    totalUnPaidMoney,
    adminRole,
    buttonExportCsv,
    findDataExportCsv,
    startDateRef,
    endDateRef,
    handleFilterData,
    showInMoney,
    setShowInMoney,
    dataReportStatistic,
  };
};
export default useReportController;
